import { createReducer, on } from '@ngrx/store';
import * as AppActions from './app.actions';
import { NousToken } from '@app/shared/models/nous-token.model';
import { setInLocalStorage, getFromLocalStorage } from '../utils/localstorage-utils';
import { ChannelDto, MetaDto, OrganizationDetailDto, OrganizationDto } from '@api/organization';
import { ChannelIdToCode } from '@app/shared/models/channel-to-code';
import { Child } from '@app/shared/models/child';

export interface State {
  token: NousToken;
  error: any;
  textSize: string;
  downloadContent: boolean;
  darkMode: boolean;
  downloadProgress: number;
  downloadCount: number;
  downloadComplete: boolean;
  organization: OrganizationDto;
  organizationDetail: OrganizationDetailDto;
  dynamicContentUrl: string;
  isInhouse: boolean;
  disableFavorites: boolean;
  disableShare: boolean;
  language: string;
  channels: ChannelDto[];
  channelSelected: ChannelDto;
  meta: MetaDto,
  userId: string;
  channelsUnlocked: ChannelIdToCode[];
  children: Child[];
  selectedChildId: string;
  selectedName: string,
  avatarXp: number,
  avatarLevel: number,
  acceptedEula: boolean,
  readInstructions: boolean,
  isFirstStart: boolean,
  doneActivities: string[],
  visitedLexiconIds: string[],
  finishAnimationShown: boolean
}

export const initialState: State = {
  token: undefined,
  error: undefined,
  textSize: getTextSizeFromLocalStorage(),
  downloadContent: getDownloadContentFromLocalStorage(),
  darkMode: getDarkModeFromLocalStorage(),
  downloadProgress: undefined,
  downloadCount: undefined,
  downloadComplete: false,
  organization: getOrganizationFromLocalStorage(),
  organizationDetail: getOrganizationDetailFromLocalStorage(),
  dynamicContentUrl: getDynamicContentUrlFromLocalStorage(),
  isInhouse: getIsInhouseFromLocalStorage(),
  disableFavorites: getDisableFavoritesFromLocalStorage(),
  disableShare: getDisableShareFromLocalStorage(),
  language: undefined,
  channels: undefined,
  channelSelected: undefined,
  meta: undefined,
  userId: getUserIdFromLocalStorage(),
  channelsUnlocked: getUnlockedChannelsFromLocalStorage(),
  children: getChildrenFromLocalStorage(),
  selectedChildId: getSelectedChildIdFromLocalStorage(),
  selectedName: getSelectedNameFromLocalStorage(),
  avatarXp: getAvatarXpFromLocalStorage(),
  avatarLevel: getAvatarLevelFromLocalStorage(),
  acceptedEula: getAcceptedEulaFromLocalStorage(),
  readInstructions: getReadInstructionsFromLocalStorage(),
  isFirstStart: getIsFirstStartFromLocalStorage(),
  doneActivities: getDoneActivitiesFromLocalStorage(),
  visitedLexiconIds: getVisititedLexiconIdsFromLocalStorage(),
  finishAnimationShown: getFinishAnimationShownFromLocalStorage(),
};

export const reducer = createReducer(
  initialState,
  on(AppActions.setToken, (state, action) =>
    ({ ...state, token: action.token })
  ),
  on(AppActions.setTextSize, (state, action) => {
    setInLocalStorage('textSize', action.textSize);
    return ({ ...state, textSize: action.textSize });
  }),
  on(AppActions.setDownloadContent, (state, action) => {
    setInLocalStorage('downloadContent', action.downloadContent);
    return ({ ...state, downloadContent: action.downloadContent });
  }),
  on(AppActions.setDarkMode, (state, action) => {
    setInLocalStorage('darkMode', action.darkMode);
    return ({ ...state, darkMode: action.darkMode });
  }),
  on(AppActions.setDownloadProgress, (state, action) => {
    return ({ ...state, downloadProgress: action.progress, downloadCount: action.count });
  }),
  on(AppActions.setDownloadCount, (state, action) => {
    return ({ ...state, downloadCount: action.count });
  }),
  on(AppActions.setDownloadComplete, (state, action) => {
    return ({ ...state, downloadComplete: action.complete });
  }),
  on(AppActions.setOrganization, (state, action) => {
    setInLocalStorage('organization', action.organization);
    return ({ ...state, organization: action.organization });
  }),
  on(AppActions.setOrganizationDetail, (state, action) => {
    setInLocalStorage('organizationDetail', action.organizationDetail);
    return ({ ...state, organizationDetail: action.organizationDetail });
  }),
  on(AppActions.setDynamicContentUrl, (state, action) => {
    setInLocalStorage('dynamicContentUrl', action.contentUrl);
    return ({ ...state, dynamicContentUrl: action.contentUrl });
  }),
  on(AppActions.setIsInhouse, (state, action) => {
    setInLocalStorage('inHouse', action.isInhouse);
    return ({ ...state, isInhouse: action.isInhouse });
  }),
  on(AppActions.setDisableFavorites, (state, action) => {
    setInLocalStorage('disableFavorites', action.disableFavorites);
    return ({ ...state, disableFavorites: action.disableFavorites });
  }),
  on(AppActions.setDisableShare, (state, action) => {
    setInLocalStorage('disableShare', action.disableShare);
    return ({ ...state, disableShare: action.disableShare });
  }),
  on(AppActions.setChannels, (state, action) => {
    return ({ ...state, channels: action.channels });
  }),
  on(AppActions.selectChannel, (state, action) => {
    return ({ ...state, channelSelected: action.channel });
  }),
  on(AppActions.setMeta, (state, action) => {
    return ({ ...state, meta: action.meta });
  }),
  on(AppActions.setUserId, (state, action) => {
    setInLocalStorage('userId', action.userId);
    return ({ ...state, userId: action.userId });
  }),
  on(AppActions.addUnlockedChannel, (state, action) => {
    const updatedChannelsUnlocked =
    !state.channelsUnlocked.some(channel => channel.channelId === action.channelId && channel.code === action.code)
      ? [...state.channelsUnlocked, {channelId: action.channelId, code: action.code}]
      : [...state.channelsUnlocked];
    setInLocalStorage('channelsUnlocked', updatedChannelsUnlocked);
    return ({ ...state, channelsUnlocked: updatedChannelsUnlocked });
  }),
  on(AppActions.setAcceptedEula, (state, action) => {
    setInLocalStorage('acceptedEula', action.acceptedEula);
    return ({ ...state, acceptedEula: action.acceptedEula });
  }),
  on(AppActions.setReadInstructions, (state, action) => {
    setInLocalStorage('readInstructions', action.readInstructions);
    return ({ ...state, readInstructions: action.readInstructions });
  }),
  on(AppActions.setFirstStart, (state, action) => {
    setInLocalStorage('firstStart', action.firstStart);
    return ({ ...state, isFirstStart: action.firstStart });
  }),
  on(AppActions.setSelectedName, (state, action) => {
    setInLocalStorage('selectedName', action.name);
    return ({ ...state, selectedName: action.name });
  }),
  on(AppActions.increaseAvatarXp, (state, action) => {
    const updatedAvatarXp = state.avatarXp + action.xp
    setInLocalStorage('avatarXp', updatedAvatarXp);
    return ({ ...state, avatarXp: updatedAvatarXp });
  }),
  on(AppActions.setAvatarLevel, (state, action) => {
    setInLocalStorage('avatarLevel', action.avatarLevel);
    return ({ ...state, avatarLevel: action.avatarLevel });
  }),
  on(AppActions.addDoneActivity, (state, action) => {
    const updatedActivities = [...state.doneActivities, action.stationId];
    setInLocalStorage('doneActivities', updatedActivities);
    return ({ ...state, doneActivities: [...state.doneActivities, action.stationId] });
  }),
  on(AppActions.addVisitedLexiconId, (state, action) => {
    const updatedActivities = [...state.visitedLexiconIds, action.lexiconId];
    setInLocalStorage('visitedLexiconIds', updatedActivities);
    return ({ ...state, visitedLexiconIds: [...state.visitedLexiconIds, action.lexiconId] });
  }),
  on(AppActions.setFinishAnimationShown, (state, action) => {
    setInLocalStorage('finishAnimationShown', action.finishAnimationShown);
    return ({ ...state, finishAnimationShown: action.finishAnimationShown });
  }),
  on(AppActions.addChild, (state, action) => {
    const updatedChildren = state.children.some(c => c.id === action.child.id)
      ? state.children.map(c =>
          c.id === action.child.id ? action.child : c
        )
      : [...state.children, action.child];
    setInLocalStorage('children', updatedChildren);
    return ({ ...state, children: updatedChildren });
  }),
  on(AppActions.removeChild, (state, action) => {
    const updatedChildren = state.children.filter(child => child.id !== action.childId);
    setInLocalStorage('children', updatedChildren);
    return ({ ...state, children: updatedChildren });
  }),
  on(AppActions.setSelectedChildId, (state, action) => {
    setInLocalStorage('selectedChildId', action.selectedChildId);
    return ({ ...state, selectedChildId: action.selectedChildId });
  }),
);

function getDarkModeFromLocalStorage(): boolean {
  const darkMode = getFromLocalStorage('darkMode');
  if (darkMode === 'true') {
    return true;
  } else {
    return false;
  }
}

function getDownloadContentFromLocalStorage(): boolean {
  const downloadContent = getFromLocalStorage('downloadContent');
  if (!downloadContent || downloadContent === 'true') {
    return true;
  } else {
    return false;
  }
}

function getTextSizeFromLocalStorage() {
  const textSize = getFromLocalStorage('textSize');
  if (textSize) {
    return textSize;
  }
  return 'small';
}

function getIsInhouseFromLocalStorage(): boolean {
  const downloadContent = getFromLocalStorage('inHouse');
  if (downloadContent === 'true') {
    return true;
  } else {
    return false;
  }
}

function getDisableFavoritesFromLocalStorage(): boolean {
  const disableFavorites = getFromLocalStorage('disableFavorites');
  if (disableFavorites === 'true') {
    return true;
  } else {
    return false;
  }
}

function getDisableShareFromLocalStorage(): boolean {
  const disableShare = getFromLocalStorage('disableShare');
  if (disableShare === 'true') {
    return true;
  } else {
    return false;
  }
}


function getDynamicContentUrlFromLocalStorage(): string {
  const dynamicContentUrl = getFromLocalStorage('dynamicContentUrl');
  return dynamicContentUrl;
}

function getAcceptedEulaFromLocalStorage(): boolean {
  const acceptedEula = getFromLocalStorage('acceptedEula');
  if (acceptedEula === 'true') {
    return true;
  } else {
    return false;
  }
}

function getReadInstructionsFromLocalStorage(): boolean {
  const readInstructions = getFromLocalStorage('readInstructions');
  if (readInstructions === 'true') {
    return true;
  } else {
    return false;
  }
}

function getIsFirstStartFromLocalStorage(): boolean {
  const firstStart = getFromLocalStorage('firstStart');
  if (firstStart === 'false') {
    return false;
  } else {
    return true;
  }
}

function getSelectedNameFromLocalStorage(): string {
  const selectedName = getFromLocalStorage('selectedName');
  return selectedName;
}

function getAvatarXpFromLocalStorage(): number {
  const avatarXp = getFromLocalStorage('avatarXp');
  if (!avatarXp) {
    return 0;
  } else {
    return avatarXp;
  }
}

function getAvatarLevelFromLocalStorage(): number {
  const avatarLevel = getFromLocalStorage('avatarLevel');
  if (!avatarLevel) {
    return 0;
  } else {
    return avatarLevel;
  }
}

function getOrganizationFromLocalStorage(): OrganizationDto {
  const organization = getFromLocalStorage('organization');
  return organization;
}

function getOrganizationDetailFromLocalStorage(): OrganizationDetailDto {
  const organizationDetail = getFromLocalStorage('organizationDetail');
  return organizationDetail;
}

function getUserIdFromLocalStorage(): string {
  const userId = getFromLocalStorage('userId');
  return userId;
}

function getChildrenFromLocalStorage(): Child[] {
  const children = getFromLocalStorage('children');
  if (children) {
    return children;
  } else {
    return [];
  }
}

function getSelectedChildIdFromLocalStorage(): string {
  const id = getFromLocalStorage('selectedChildId');
  return id;
}

function getUnlockedChannelsFromLocalStorage(): ChannelIdToCode[] {
  const unlockedChannels = getFromLocalStorage('channelsUnlocked');
  if (unlockedChannels) {
    return unlockedChannels;
  } else {
    return [];
  }
}

function getDoneActivitiesFromLocalStorage(): string[] {
  const doneActivities = getFromLocalStorage('doneActivities');
  if (doneActivities) {
    return doneActivities;
  } else {
    return [];
  }
}

function getVisititedLexiconIdsFromLocalStorage(): string[] {
  const visitedLexiconIds = getFromLocalStorage('visitedLexiconIds');
  if (visitedLexiconIds) {
    return visitedLexiconIds;
  } else {
    return [];
  }
}

function getFinishAnimationShownFromLocalStorage(): boolean {
  const finishAnimationShown = getFromLocalStorage('finishAnimationShown');
  if (finishAnimationShown === 'true') {
    return true;
  } else {
    return false;
  }
}
